import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { shortenStringWithTooltip } from '../helper';

function TabMenu({
  MenuList,
  selectIndex,
  selectionTabs,
  handleRadioButton,
  handleChangeSwitch,
  isSupplierLoggedIn,
  isProductArchived,
  unreadCount,
  fromMeasurements,
  editAccess,
  selectionAccess,
  fromProductIdentifier,
  loading,
  isSample,
  isFullWidth,
}) {
  const [greedyMenuList, setGreedyMenuList] = useState([]);
  const [activeUnderline, setActiveUnderline] = useState({});
  const { t } = useTranslation('common');

  const navRef = useRef(null);
  const btnRef = useRef(null);
  const vlinksRef = useRef(null);
  const hlinksRef = useRef(null);
  const breaksRef = useRef([]);
  const timerIdRef = useRef(0);
  const counterRef = useRef(0);

  const renderTabs = useMemo(() => {
    if (isSample) {
      return isSupplierLoggedIn ? greedyMenuList.filter((menu) => menu?.showTab === true) : greedyMenuList;
    }
    return isSupplierLoggedIn ? greedyMenuList.filter((menu) => menu?.visibleToSupplier === true) : greedyMenuList;
  }, [isSupplierLoggedIn, greedyMenuList, isSample]);

  useEffect(() => {
    if (renderTabs.length > 0) {
      const currentTabExists = renderTabs.some((tab) => tab._id === selectIndex);
      if (!currentTabExists) {
        const firstTabId = renderTabs[0]._id;
        localStorage.setItem('activeTabId', firstTabId);
        selectionTabs(firstTabId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderTabs, selectIndex]);

  const updateNav = useCallback(() => {
    timerIdRef.current = setTimeout(() => {
      try {
        const nav = navRef.current;
        const btn = btnRef.current;
        const vlinks = vlinksRef.current;
        const hlinks = hlinksRef.current;
        const breaks = breaksRef.current;

        if (nav && btn && vlinks && hlinks) {
          const availableSpace = btn.classList.contains('hidden') ? nav.clientWidth : nav.clientWidth - btn.clientWidth;
          if (vlinks.clientWidth > availableSpace) {
            if (vlinks.lastChild) {
              breaks.push(vlinks.clientWidth);
              hlinks.prepend(vlinks.lastChild);
            }

            if (btn.classList.contains('hidden')) {
              btn.classList.remove('hidden');
            }
          } else {
            if (availableSpace > breaks[breaks.length - 1] && hlinks.lastChild) {
              vlinks.append(hlinks.firstChild);
              breaks.pop();
            }
            if (breaks.length < 1) {
              btn.classList.add('hidden');
              hlinks.classList.add('hidden');
            }
          }
          btn.setAttribute('count', breaks.length);
          if (vlinks.clientWidth > availableSpace) {
            updateNav();
          }
        } else {
          counterRef.current++;
          if (counterRef.current < 2) window.location.reload();
        }
      } catch (error) {
        console.log('updateNav error', error);
      }
    }, 100);
  }, []);

  const toggleHidden = () => {
    try {
      const hlinks = hlinksRef.current;
      if (hlinks) {
        hlinks.classList.toggle('hidden');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (timerIdRef.current) clearTimeout(timerIdRef.current);
    setGreedyMenuList(MenuList);
    updateNav();
    window.addEventListener('resize', updateNav);
    return () => {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
      window.removeEventListener('resize', updateNav);
    };
  }, [MenuList, updateNav]);

  useEffect(() => {
    if (vlinksRef.current && selectIndex && renderTabs?.length > 0) {
      const activeTab = vlinksRef.current.querySelector('.active');

      if (activeTab) {
        const tabRect = activeTab.getBoundingClientRect();
        const parentRect = vlinksRef.current.getBoundingClientRect();

        setActiveUnderline({
          width: tabRect.width,
          left: tabRect.left - parentRect.left,
        });
      } else {
        setActiveUnderline({
          width: 0,
          display: 'none',
        });
      }
    }
  }, [selectIndex, renderTabs]);

  useEffect(() => {
    const breaksAtMount = breaksRef;
    return () => {
      breaksAtMount.current = [];
    };
  }, []);

  return (
    <nav className="greedy-nav" style={{ width: isFullWidth ? '100%' : 'auto' }} ref={navRef}>
      <ul className="subnav-ul visible-links" ref={vlinksRef}>
        {renderTabs?.length > 0
          ? renderTabs?.map((element, index) => {
              return (
                <React.Fragment key={element?._id}>
                  <li className={selectIndex === element._id ? ' active' : ''}>
                    <button type="button" style={loading ? { pointerEvents: 'none', opacity: '0.5' } : {}} disabled={loading}>
                      <div className="tab-item">
                        {!isSupplierLoggedIn &&
                          !element?.name?.includes('Supplier') &&
                          selectionAccess &&
                          !fromProductIdentifier &&
                          element?.isSupplierSwitch && (
                            <div className="d-flex align-items-center pl-0 design-switch">
                              <input
                                type="checkbox"
                                className="form-check-input bom-switch-input"
                                id={`customSwitchmenu${index}`}
                                checked={element.switch}
                                disabled={isProductArchived || !selectionAccess}
                                onChange={(e) => {
                                  handleChangeSwitch(e, element);
                                }}
                              />
                            </div>
                          )}
                        <span
                          className="d-flex text-bold ml-0 align-items-center"
                          disabled={isProductArchived}
                          onClick={() => {
                            selectionTabs(element?._id ? element?._id : '');
                            if (hlinksRef.current && !hlinksRef.current.classList.contains('hidden')) {
                              hlinksRef.current.classList.add('hidden');
                            }
                          }}
                        >
                          {shortenStringWithTooltip(element.name, 18)}
                          {element.showBadge && unreadCount > 0 && (
                            <Badge className="comment-count ml-1" bg="secondary">
                              {unreadCount}
                            </Badge>
                          )}
                        </span>

                        {!isSupplierLoggedIn && fromMeasurements && element?._id === selectIndex && editAccess && !isProductArchived && (
                          <a
                            target="_blank"
                            title={t('GREEDY_MENU_TABS.EDIT')}
                            className="spec-edit-icon d-flex"
                            href={`${window.location.origin}/libraries/gradings/edit/${selectIndex}`}
                            rel="noreferrer"
                          >
                            <span className="icon-Edit-1  f-24" />
                          </a>
                        )}
                        {!isSupplierLoggedIn && fromMeasurements && (
                          <div className="custom-radio no-label radio-dark mb-0 ml-8">
                            <input
                              type="radio"
                              id={`customRadioButton${index}`}
                              name={`customRadioButton${index}`}
                              checked={element?.isDefault}
                              disabled={!selectionAccess || isProductArchived}
                              onChange={(e) => {
                                handleRadioButton(e, element);
                              }}
                            />
                            <label
                              htmlFor={`customRadioButton${index}`}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={t('GREEDY_MENU_TABS.SET_US_DEFAULT')}
                              className="tooltip-wrapper chklabel align-items-start mb-3"
                              disabled={isProductArchived}
                            />
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                </React.Fragment>
              );
            })
          : null}
      </ul>
      <button type="button" className="btn-more-dropdown hidden" onClick={toggleHidden} ref={btnRef}>
        <span className="icon-More f-32 text-black" />
      </button>
      <ul className="hidden-links hidden" ref={hlinksRef} />
      <div className="tab-underline" style={activeUnderline} />
    </nav>
  );
}

export default TabMenu;
